/**
 * UTM Parameter Handling
 *
 * Captures UTM parameters from URL and stores them in sessionStorage
 * for later use in tracking events.
 */

/**
 * Toggle for sending the "UTM Captured" event.
 * Set to true to re-enable sending. UTM params are still captured
 * and stored in sessionStorage regardless of this setting.
 */
export const SEND_UTM_CAPTURED_EVENT = false;

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid'
];
const UTM_STORAGE_KEY = 'lb-utm-params';

/**
 * Capture UTM parameters from current URL and store in sessionStorage
 * @returns {Object|null} UTM data if found, null otherwise
 */
export function captureUtmParams() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmData = {};
    let hasUtm = false;

    UTM_PARAMS.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUtm = true;

        // If gclid exists, the referrer is Google Ads
        if (param === 'gclid') {
          utmData.utm_source = 'google';
          utmData.utm_medium = 'cpc';
        }
      }
    });

    if (hasUtm) {
      const storageData = {
        ...utmData,
        landingPage: window.location.pathname
      };
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(storageData));
      return storageData;
    }

    return null;
  } catch (e) {
    console.error('Error capturing UTM params:', e);
    return null;
  }
}

/**
 * Get stored UTM parameters from sessionStorage
 * @returns {Object|null} Stored UTM data or null
 */
export function getStoredUtmParams() {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('Error getting stored UTM params:', e);
    return null;
  }
}

/**
 * Clear stored UTM parameters
 */
export function clearUtmParams() {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing UTM params:', e);
  }
}

/**
 * Check if there are any stored UTM parameters
 * @returns {boolean}
 */
export function hasStoredUtmParams() {
  return getStoredUtmParams() !== null;
}

export default {
  captureUtmParams,
  getStoredUtmParams,
  clearUtmParams,
  hasStoredUtmParams
};
